<template>
    <BasePopup>
        <div
            v-bind:class="['tutorial', viewGaClass]"
            v-bind:style="{ zIndex: zIndexMapping[zIndex] }"
        >
            <div class="tutorialMask" />
            <slot name="backdrop" />

            <div class="multiplePopup-body" v-bind:class="{ _centered: isCentered }">
                <div class="tutorialBubble" v-bind:class="{ _noArrow: isCentered }">
                    <div class="tutorialBubble-header">
                        <h2 class="tutorialBubble-title">
                            {{ currentStep.title }}
                        </h2>
                        <button
                            v-if="showCloseButton"
                            v-bind:class="['tutorialBubble-close', closeButtonGaClass]"
                            type="button"
                            aria-label="Close"
                            v-on:click="emit('callback')"
                        >
                            <LazyIcon name="system:cross" />
                        </button>
                    </div>
                    <p class="tutorialBubble-description">
                        {{ currentStep.description }}
                    </p>
                    <div class="tutorialBubble-footer">
                        <span class="tutorialBubble-step">{{ stepIndex + 1 }} / {{ steps.length }}</span>
                        <div class="tutorialBubble-actions">
                            <button
                                v-if="stepIndex > 0"
                                v-bind:class="['tutorialBubble-back', backButtonGaClass]"
                                type="button"
                                v-on:click="goToPreviousStep"
                            >
                                {{ backText }}
                            </button>
                            <button
                                v-bind:class="['tutorialBubble-next', nextButtonGaClass]"
                                type="button"
                                v-on:click="handleNextClick"
                            >
                                {{ nextButtonText }}
                            </button>
                        </div>
                    </div>
                </div>
                <slot name="content" />
            </div>

            <slot name="overlay" />
        </div>
    </BasePopup>
</template>

<script lang="ts" setup>
    import { LazyIcon } from '@sbo/icons-vue';
    import { computed } from 'vue';
    import BasePopup from '@/components/common/BasePopup.vue';
    import type { ZIndex } from '@/interface/enum';
    import { zIndexMapping } from '@/libraries/helpers/zIndexHelper';
    import { i18n } from '@/libraries/i18n';

    interface IPopupStep {
        title: string;
        description: string;
    }

    const props = withDefaults(defineProps<{
        steps: IPopupStep[];
        zIndex: ZIndex;
        gaClassPrefix?: string;
        showCloseButton?: boolean;
        isCentered?: boolean;
    }>(), {
        gaClassPrefix: '',
        showCloseButton: false,
        isCentered: false,
    });

    const emit = defineEmits<{
        callback: [];
    }>();

    const stepIndex = defineModel<number>('stepIndex', { default: 0 });

    const currentStep = computed(() => props.steps[stepIndex.value]);
    const isLastStep = computed(() => stepIndex.value === props.steps.length - 1);
    const backText = computed(() => i18n.t('comm_back'));
    const nextButtonText = computed(() => (isLastStep.value
        ? i18n.t('comm_close')
        : i18n.t('comm_next')));
    const viewGaClass = computed(() => props.gaClassPrefix);
    const backButtonGaClass = computed(() => getGaClass('Back'));
    const closeButtonGaClass = computed(() => getGaClass('CloseIcon'));
    const nextButtonGaClass = computed(() => getGaClass(isLastStep.value ? 'Close' : 'Next'));

    function getGaClass(action: string) {
        return props.gaClassPrefix ? `${props.gaClassPrefix}_${action}` : '';
    }

    function goToPreviousStep() {
        stepIndex.value -= 1;
    }

    function handleNextClick() {
        if (isLastStep.value) {
            emit('callback');
            return;
        }

        stepIndex.value += 1;
    }
</script>

<style lang="scss" scoped>
@import "@/scss/components/multiplePopup";
</style>
